import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Film, Plus, Settings, FolderOpen, LogOut, Bell, Check, X, ChevronRight, AtSign, Mic2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [invites, setInvites] = useState([]);
  const [teamInvites, setTeamInvites] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [mentionUnread, setMentionUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [teamsRes, invRes, teamInvRes, mentionsRes] = await Promise.all([
        api.get('/teams'),
        api.get('/invites'),
        api.get('/team-invites'),
        api.get('/chat/mentions'),
      ]);
      setTeams(teamsRes.data);
      setActiveTeam(teamsRes.data.find(t => t.is_active));
      setInvites(invRes.data);
      setTeamInvites(teamInvRes.data);
      setMentions(mentionsRes.data.mentions || []);
      setMentionUnread(mentionsRes.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => { loadData(); const i = setInterval(loadData, 15000); return () => clearInterval(i); }, [loadData]);

  useEffect(() => {
    const onUpdate = () => loadData();
    window.addEventListener('mentions-updated', onUpdate);
    return () => window.removeEventListener('mentions-updated', onUpdate);
  }, [loadData]);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const jumpToMention = (m) => {
    setNotifOpen(false);
    window.dispatchEvent(new CustomEvent('open-chat-channel', {
      detail: { scope: m.scope, ref_id: m.ref_id, name: m.channel_name, messageId: m.message_id },
    }));
    if (m.scope === 'project') navigate(`/project/${m.ref_id}`);
    setTimeout(loadData, 1200);
  };

  const handleSwitchTeam = async (teamId) => {
    const alreadyActive = teams.find(t => t.id === teamId)?.is_active;
    if (!alreadyActive) {
      try {
        await api.put(`/auth/active-team?team_id=${teamId}`);
        await loadData();
        window.dispatchEvent(new Event('workspace-changed'));
      } catch { toast.error('Failed to switch'); }
    } else {
      window.dispatchEvent(new Event('workspace-changed'));
    }
    navigate('/dashboard');
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      await api.post('/teams', { name: newTeamName.trim() });
      setNewTeamName(''); setShowNewTeam(false);
      await loadData();
      toast.success('Team created!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAcceptProjectInvite = async (id) => {
    try {
      const res = await api.post(`/invites/${id}/accept`);
      setInvites(prev => prev.filter(i => i.id !== id));
      toast.success('Project invite accepted!');
      navigate(`/project/${res.data.project_id}`);
    } catch { toast.error('Failed'); }
  };

  const handleAcceptTeamInvite = async (id) => {
    try {
      await api.post(`/team-invites/${id}/accept`);
      setTeamInvites(prev => prev.filter(i => i.id !== id));
      toast.success('Workspace invite accepted!');
      await loadData();
      window.dispatchEvent(new Event('workspace-changed'));
      setNotifOpen(false);
      navigate('/dashboard');
    } catch { toast.error('Failed'); }
  };

  const handleMarkAllMentionsRead = async () => {
    try {
      await api.post('/chat/mentions/read-all');
      await loadData();
      window.dispatchEvent(new Event('mentions-updated'));
    } catch { toast.error('Failed'); }
  };

  const allInvites = [...invites, ...teamInvites];
  const isActive = (path) => location.pathname === path;
  const isProjectPage = location.pathname.startsWith('/project/');

  return (
    <>
      {/* ── DESKTOP SIDEBAR (hidden on mobile) ── */}
      <div className="hidden md:flex fixed left-0 top-0 bottom-0 w-[68px] bg-[#0A0A0E] border-r border-white/[0.04] flex-col items-center z-50" data-testid="sidebar">
        {/* Logo */}
        <button onClick={() => navigate('/dashboard')} className="w-full py-4 flex justify-center hover:opacity-80 transition-opacity" data-testid="sidebar-logo">
          <div className="w-10 h-10 bg-[#FF4500] rounded-xl flex items-center justify-center">
            <Film className="w-5 h-5 text-white" />
          </div>
        </button>

        {/* Divider */}
        <div className="w-8 h-px bg-white/[0.06] my-1" />

        {/* Team Icons */}
        <div className="flex-1 w-full overflow-y-auto py-2 space-y-2 flex flex-col items-center scrollbar-hide">
          {teams.map(team => (
            <button
              key={team.id}
              data-testid={`sidebar-team-${team.id}`}
              onClick={() => handleSwitchTeam(team.id)}
              title={team.name}
              className={`w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-200 relative group ${
                team.is_active
                  ? 'bg-[#FF4500]/15 text-[#FF4500] ring-2 ring-[#FF4500]/40'
                  : 'bg-white/[0.04] text-[#55555C] hover:bg-white/[0.08] hover:text-[#8A8A93]'
              }`}
            >
              {team.name.charAt(0).toUpperCase()}
              {team.is_active && (
                <div className="absolute -left-[2px] top-1/2 -translate-y-1/2 w-1 h-5 bg-[#FF4500] rounded-r-full" />
              )}
              <div className="absolute left-full ml-3 px-3 py-1.5 bg-[#1A1A20] border border-white/10 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-[60] shadow-xl">
                {team.name}
                {team.is_active && <span className="text-[#FF4500] ml-1">(active)</span>}
              </div>
            </button>
          ))}

          {/* Add Team */}
          <DropdownMenu open={showNewTeam} onOpenChange={setShowNewTeam}>
            <DropdownMenuTrigger asChild>
              <button data-testid="sidebar-add-team" title="New team"
                className="w-11 h-11 rounded-xl flex items-center justify-center text-[#55555C] hover:text-white hover:bg-white/[0.04] border border-dashed border-white/[0.08] hover:border-white/20 transition-all">
                <Plus className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="bg-[#1A1A20] border-white/10 min-w-[220px] p-3">
              <p className="text-xs text-[#8A8A93] mb-2 font-medium">New Workspace</p>
              <div className="flex gap-1.5">
                <input
                  data-testid="sidebar-new-team-input"
                  type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                  className="ff-input text-xs flex-1 py-1.5" placeholder="Team name"
                  onKeyDown={e => e.key === 'Enter' && handleCreateTeam()}
                  autoFocus
                />
                <button onClick={handleCreateTeam} className="px-2.5 py-1.5 bg-[#FF4500] hover:bg-[#E03C00] rounded-lg text-white text-xs font-medium">
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

        </div>

        {/* Bottom Section */}
        <div className="w-full flex flex-col items-center py-3 space-y-2 border-t border-white/[0.04]">
          {/* Presenter mode */}
          <button data-testid="sidebar-presenter" title="Presenter mode — talking-head videos" onClick={() => navigate('/presenter')}
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all relative group ${
              isActive('/presenter') ? 'bg-[#E879F9]/15 text-[#E879F9] ring-2 ring-[#E879F9]/40' : 'text-[#55555C] hover:text-[#E879F9] hover:bg-[#E879F9]/10'
            }`}>
            <Mic2 className="w-4.5 h-4.5" />
            <div className="absolute left-full ml-3 px-3 py-1.5 bg-[#1A1A20] border border-white/10 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-[60] shadow-xl">Presenter mode</div>
          </button>
          {/* Notifications */}
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <button data-testid="sidebar-notifications" title="Notifications"
                className="w-11 h-11 rounded-xl flex items-center justify-center text-[#55555C] hover:text-white hover:bg-white/[0.04] transition-all relative">
                <Bell className="w-4.5 h-4.5" />
                {(allInvites.length + mentionUnread) > 0 && (
                  <span data-testid="notif-badge" className="absolute top-1.5 right-1.5 min-w-4 h-4 px-1 bg-[#FF4500] rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                    {allInvites.length + mentionUnread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="bg-[#1A1A20] border-white/10 min-w-[300px] max-h-[70vh] overflow-y-auto">
              <div className="px-3 py-2"><p className="text-sm font-medium text-white">Notifications</p></div>
              <DropdownMenuSeparator className="bg-white/10" />
              {allInvites.length === 0 && mentions.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-[#55555C]">You're all caught up</div>
              ) : (
                <>
                  {invites.map(inv => (
                    <div key={inv.id} className="px-3 py-2.5 hover:bg-white/5">
                      <p className="text-xs text-white mb-1"><span className="font-medium">{inv.inviter_name}</span> <span className="text-[#8A8A93]">invited you to project</span></p>
                      <p className="text-xs text-[#FF4500] font-medium mb-2">{inv.project_name}</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleAcceptProjectInvite(inv.id)} className="flex-1 py-1 text-[10px] font-medium rounded bg-[#FF4500] text-white flex items-center justify-center gap-1"><Check className="w-2.5 h-2.5" />Accept</button>
                        <button onClick={() => setInvites(p => p.filter(i => i.id !== inv.id))} className="flex-1 py-1 text-[10px] font-medium rounded bg-white/5 text-[#8A8A93] flex items-center justify-center gap-1"><X className="w-2.5 h-2.5" />Decline</button>
                      </div>
                    </div>
                  ))}
                  {teamInvites.map(inv => (
                    <div key={inv.id} className="px-3 py-2.5 hover:bg-white/5">
                      <p className="text-xs text-white mb-1"><span className="font-medium">{inv.inviter_name}</span> <span className="text-[#8A8A93]">invited you to team</span></p>
                      <p className="text-xs text-[#FF4500] font-medium mb-2">{inv.team_name}</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleAcceptTeamInvite(inv.id)} className="flex-1 py-1 text-[10px] font-medium rounded bg-[#FF4500] text-white flex items-center justify-center gap-1"><Check className="w-2.5 h-2.5" />Accept</button>
                        <button onClick={() => setTeamInvites(p => p.filter(i => i.id !== inv.id))} className="flex-1 py-1 text-[10px] font-medium rounded bg-white/5 text-[#8A8A93] flex items-center justify-center gap-1"><X className="w-2.5 h-2.5" />Decline</button>
                      </div>
                    </div>
                  ))}
                  {mentions.length > 0 && (
                    <>
                      {allInvites.length > 0 && <DropdownMenuSeparator className="bg-white/10" />}
                      <div className="px-3 py-1.5 flex items-center gap-1.5">
                        <AtSign className="w-3 h-3 text-[#A78BFA]" />
                        <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93]">Mentions</p>
                        {mentionUnread > 0 && (
                          <button
                            data-testid="mark-all-mentions-read"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMarkAllMentionsRead(); }}
                            className="ml-auto text-[10px] font-medium text-[#FF4500] hover:text-[#FF6A33] transition-colors"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      {mentions.map(m => (
                        <button
                          key={m.message_id}
                          data-testid={`mention-notif-${m.message_id}`}
                          onClick={() => jumpToMention(m)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors border-l-2 ${m.unread ? 'border-[#FF4500] bg-[#FF4500]/[0.04]' : 'border-transparent'}`}
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-xs font-medium text-white truncate">{m.sender_name}</span>
                            <span className="text-[10px] text-[#8A8A93]">in {m.channel_name}</span>
                            <span className="text-[10px] text-[#55555C] ml-auto shrink-0">{timeAgo(m.created_at)}</span>
                          </div>
                          <p className="text-xs text-[#A1A1AA] line-clamp-2 leading-snug">{m.text || 'mentioned you'}</p>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Settings */}
          <button data-testid="sidebar-settings" title="Team Settings" onClick={() => navigate('/settings')}
            className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              isActive('/settings') ? 'bg-white/[0.08] text-white' : 'text-[#55555C] hover:text-white hover:bg-white/[0.04]'
            }`}>
            <Settings className="w-4.5 h-4.5" />
          </button>

          {/* User Avatar / Logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="sidebar-user" title={user?.name || user?.email}
                className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#FF4500]/10 text-[#FF4500] text-sm font-bold hover:bg-[#FF4500]/20 transition-all">
                {(user?.name || user?.email || '?')[0].toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="bg-[#1A1A20] border-white/10 min-w-[180px]">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-[#55555C]">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="sidebar-logout" onClick={() => { logout(); navigate('/login'); }}
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── MOBILE BOTTOM NAV (hidden on desktop) ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-[#0A0A0E]/95 backdrop-blur-lg border-t border-white/[0.06] flex items-center justify-around z-50 px-2" data-testid="mobile-nav">
        <button onClick={() => navigate('/dashboard')} data-testid="mobile-nav-dashboard"
          className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive('/dashboard') ? 'text-[#FF4500]' : 'text-[#55555C]'}`}>
          <FolderOpen className="w-5 h-5" />
          <span className="text-[10px] font-medium">Workspace</span>
        </button>
        <button onClick={() => navigate('/presenter')} data-testid="mobile-nav-presenter"
          className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive('/presenter') ? 'text-[#E879F9]' : 'text-[#55555C]'}`}>
          <Mic2 className="w-5 h-5" />
          <span className="text-[10px] font-medium">Presenter</span>
        </button>

        {activeTeam && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-nav-team"
                className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[#55555C]">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold bg-[#FF4500]/15 text-[#FF4500]">
                  {activeTeam?.name?.charAt(0)?.toUpperCase() || 'T'}
                </div>
                <span className="text-[10px] font-medium truncate max-w-[60px]">{activeTeam?.name || 'Team'}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="center" className="bg-[#1A1A20] border-white/10 min-w-[240px] mb-2">
              <div className="px-3 py-2"><p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8A8A93]">Switch workspace</p></div>
              <DropdownMenuSeparator className="bg-white/10" />
              {teams.map(team => (
                <button
                  key={team.id}
                  data-testid={`mobile-switch-team-${team.id}`}
                  onClick={() => handleSwitchTeam(team.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 text-left transition-colors"
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${team.is_active ? 'bg-[#FF4500] text-white' : 'bg-white/[0.06] text-[#8A8A93]'}`}>
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{team.name}</p>
                    <p className="text-[10px] text-[#55555C]">{(team.members?.length || 1)} member{(team.members?.length || 1) > 1 ? 's' : ''}</p>
                  </div>
                  {team.is_active && <Check className="w-4 h-4 text-[#FF4500] shrink-0" />}
                </button>
              ))}
              <DropdownMenuSeparator className="bg-white/10" />
              <button
                data-testid="mobile-new-workspace"
                onClick={() => setShowNewTeam(true)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 text-left text-[#8A8A93] hover:text-white transition-colors"
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/[0.04]"><Plus className="w-4 h-4" /></div>
                <span className="text-sm">New workspace</span>
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="mobile-nav-notifications"
              className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[#55555C] relative">
              <Bell className="w-5 h-5" />
              {allInvites.length > 0 && (
                <span className="absolute top-0.5 right-1 w-4 h-4 bg-[#FF4500] rounded-full text-[9px] font-bold text-white flex items-center justify-center">{allInvites.length}</span>
              )}
              <span className="text-[10px] font-medium">Alerts</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="center" className="bg-[#1A1A20] border-white/10 min-w-[280px] mb-2">
            <div className="px-3 py-2"><p className="text-sm font-medium text-white">Notifications</p></div>
            <DropdownMenuSeparator className="bg-white/10" />
            {allInvites.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-[#55555C]">No pending invites</div>
            ) : (
              <>
                {invites.map(inv => (
                  <div key={inv.id} className="px-3 py-2.5 hover:bg-white/5">
                    <p className="text-xs text-white mb-1"><span className="font-medium">{inv.inviter_name}</span> <span className="text-[#8A8A93]">invited you to project</span></p>
                    <p className="text-xs text-[#FF4500] font-medium mb-2">{inv.project_name}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleAcceptProjectInvite(inv.id)} className="flex-1 py-1 text-[10px] font-medium rounded bg-[#FF4500] text-white flex items-center justify-center gap-1"><Check className="w-2.5 h-2.5" />Accept</button>
                      <button onClick={() => setInvites(p => p.filter(i => i.id !== inv.id))} className="flex-1 py-1 text-[10px] font-medium rounded bg-white/5 text-[#8A8A93] flex items-center justify-center gap-1"><X className="w-2.5 h-2.5" />Decline</button>
                    </div>
                  </div>
                ))}
                {teamInvites.map(inv => (
                  <div key={inv.id} className="px-3 py-2.5 hover:bg-white/5">
                    <p className="text-xs text-white mb-1"><span className="font-medium">{inv.inviter_name}</span> <span className="text-[#8A8A93]">invited you to team</span></p>
                    <p className="text-xs text-[#FF4500] font-medium mb-2">{inv.team_name}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleAcceptTeamInvite(inv.id)} className="flex-1 py-1 text-[10px] font-medium rounded bg-[#FF4500] text-white flex items-center justify-center gap-1"><Check className="w-2.5 h-2.5" />Accept</button>
                      <button onClick={() => setTeamInvites(p => p.filter(i => i.id !== inv.id))} className="flex-1 py-1 text-[10px] font-medium rounded bg-white/5 text-[#8A8A93] flex items-center justify-center gap-1"><X className="w-2.5 h-2.5" />Decline</button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <button onClick={() => navigate('/settings')} data-testid="mobile-nav-settings"
          className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive('/settings') ? 'text-[#FF4500]' : 'text-[#55555C]'}`}>
          <Settings className="w-5 h-5" />
          <span className="text-[10px] font-medium">Settings</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="mobile-nav-user" className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[#55555C]">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#FF4500]/10 text-[#FF4500] text-xs font-bold">
                {(user?.name || user?.email || '?')[0].toUpperCase()}
              </div>
              <span className="text-[10px] font-medium">Me</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="bg-[#1A1A20] border-white/10 min-w-[180px] mb-2">
            <div className="px-3 py-2">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-[#55555C]">{user?.email}</p>
            </div>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }}
              className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
